import { useEffect, useState, type ReactNode } from 'react';
import {
  Skeleton,
  SkeletonItem,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  getDashboardComponentData,
  getDashboardComponentOptions,
  type DashboardSource,
} from '../../../../services/dashboardService';
import PanelError from './PanelError';
import PanelRefreshButton from './PanelRefreshButton';
import PanelOptionsDropdown from './PanelOptionsDropdown';

// The dashboard service types `IChartData.values` as `Map<string, number>`, but
// the backend returns a plain JSON object at runtime (the existing Portal panels
// also read it via `Object.values`). Normalize to a record so we can iterate it.
const toRecord = (values: unknown): Record<string, number> =>
  (values ?? {}) as Record<string, number>;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: tokens.spacingVerticalM,
    gap: tokens.spacingVerticalM,
  },
  top: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    color: tokens.colorBrandForeground1,
  },
  label: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  value: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    fontSize: tokens.fontSizeHero800,
    lineHeight: tokens.lineHeightHero900,
    fontWeight: tokens.fontWeightBold,
  },
  skeletonRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
});

interface CountPanelProps {
  label: string;
  code: string;
  source: DashboardSource;
  icon: ReactNode;
  // Bumped by the parent's global Refresh to re-run fetches in place (no
  // remount), avoiding an aborted request wave.
  refreshKey?: number;
}

const CountPanel = ({
  label,
  code,
  source,
  icon,
  refreshKey = 0,
}: CountPanelProps) => {
  const styles = useStyles();
  const [options, setOptions] = useState<{ key: string; text: string }[]>();
  const [status, setStatus] = useState('');
  const [count, setCount] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  // Bumped by Retry to re-run the data fetch after a failure.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const ac = new AbortController();
    setOptions(undefined);
    setFailed(false);
    getDashboardComponentOptions(ac, code, source)
      .then((res) => {
        const opts = Object.values(toRecord(res.values)).map((value) => ({
          key: String(value),
          text: String(value),
        }));
        setOptions(opts);
        setStatus((prev) => prev || opts[0]?.key || '');
      })
      .catch(() => {
        if (ac.signal.aborted) return;
        // A failed options fetch is non-fatal: fall back to no options so the
        // data call still fires (with no `options` param). For a count that
        // truly has options the data call may then 500 and surface the error,
        // same as before but one step later; optionless counts work fine.
        setOptions([]);
      });
    return () => ac.abort();
  }, [code, source, reloadKey, refreshKey]);

  const hasOptions = (options?.length ?? 0) > 0;

  useEffect(() => {
    if (options === undefined) return;
    if (hasOptions && status === '') return;
    const ac = new AbortController();
    setCount(null);
    setFailed(false);
    const optionValue = hasOptions ? status : undefined;
    getDashboardComponentData(ac, source, code, optionValue)
      .then((res) => setCount(Object.values(toRecord(res.values))[0] ?? 0))
      .catch(() => {
        if (ac.signal.aborted) return;
        setFailed(true);
      });
    return () => ac.abort();
  }, [code, source, status, options, hasOptions]);

  // Loading while options resolve or the count fetch is in flight; never
  // "loading" once we've failed (so the in-panel refresh re-enables).
  const loading = !failed && (options === undefined || count === null);

  return (
    <div className={styles.root}>
      <div className={styles.top}>
        <div className={styles.head}>
          <span className={styles.icon}>{icon}</span>
          <Text className={styles.label}>{label}</Text>
        </div>
        <div className={styles.headerActions}>
          {hasOptions && (
            <PanelOptionsDropdown
              options={options}
              selected={status}
              onSelect={setStatus}
            />
          )}
          <PanelRefreshButton
            loading={loading}
            onRefresh={() => setReloadKey((k) => k + 1)}
          />
        </div>
      </div>
      {failed ? (
        <PanelError onRetry={() => setReloadKey((k) => k + 1)} />
      ) : count === null ? (
        <Skeleton>
          <div className={styles.skeletonRow}>
            <SkeletonItem size={40} style={{ width: '96px' }} />
          </div>
        </Skeleton>
      ) : (
        <div className={styles.value}>{count.toLocaleString()}</div>
      )}
    </div>
  );
};

export default CountPanel;
